import { memo, useEffect, useRef, useState } from 'react';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@Components/ui-v2/dropdown-menu';
import { AgentConversation } from '@Models';
import { AGENT_DURATIONS, AGENT_EASING } from '../../../Helpers/Agents/animations';

interface NavSidebarItemProps {
  conversation: AgentConversation;
  isActive: boolean;
  isLoading: boolean;
  isUnread: boolean;
  onClick: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
}

const formatTime = (timestamp: string | undefined): string => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

const NavSidebarItem = ({
  conversation,
  isActive,
  isLoading,
  isUnread,
  onClick,
  onRename,
  onDelete,
}: NavSidebarItemProps) => {
  const { t } = useTranslation();
  const title = conversation.title || t('Agent.EmptyTitle');
  const time = formatTime(conversation.timestamps?.last_interaction_at ?? conversation.timestamps?.updated_at);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const titleId = `delete-modal-title-${conversation.conversation_id}`;

  useEffect(() => {
    if (isEditing) {
      setDraft(title);
      window.setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 10);
    }
  }, [isEditing, title]);

  // Close the delete confirmation on Esc — Radix Dialog used to give this for
  // free, but we now render the modal via createPortal to escape the agent
  // shell's transform context, so we wire Esc manually.
  useEffect(() => {
    if (!isConfirmingDelete) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsConfirmingDelete(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isConfirmingDelete]);

  const commitRename = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== title) onRename(conversation.conversation_id, trimmed);
    setIsEditing(false);
  };

  const cancelRename = () => {
    setIsEditing(false);
    setDraft(title);
  };

  return (
    <div
      className={`group relative flex items-center gap-2 rounded-md px-2.5 py-1.5 transition-colors duration-200 ${
        isActive ? 'bg-[#7c6cf6]/10 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
      }`}>
      <AnimatePresence mode="wait">
        {(isLoading || isUnread) && !isEditing && (
          <motion.span
            key={isLoading ? 'loading' : 'unread'}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: AGENT_DURATIONS.fast, ease: AGENT_EASING.outExpo }}
            className="relative inline-flex h-1.5 w-1.5 flex-shrink-0"
            aria-hidden>
            {isLoading ? (
              <>
                <span className="absolute inset-0 animate-ping rounded-full bg-primary/40" />
                <span className="relative inline-block h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_5px_1.5px_rgba(98,65,234,0.5)]" />
              </>
            ) : (
              <span className="relative inline-block h-1.5 w-1.5 rounded-full bg-blue-500" />
            )}
          </motion.span>
        )}
      </AnimatePresence>

      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRename();
            else if (e.key === 'Escape') cancelRename();
          }}
          className="min-w-0 flex-1 rounded border border-[#7c6cf6]/40 bg-white px-1.5 py-0.5 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-[#7c6cf6]/30"
          style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}
        />
      ) : (
        <button
          type="button"
          onClick={() => onClick(conversation.conversation_id)}
          className="flex min-w-0 flex-1 flex-col items-start gap-0.5 overflow-hidden text-left cursor-pointer focus:outline-none">
          <span className="w-full truncate text-sm leading-tight">{title}</span>
          {time && <span className="text-[10px] leading-tight text-gray-400">{time}</span>}
        </button>
      )}

      {!isEditing && (
        <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('Agent.AriaConversationActions')}
              className="inline-flex flex-shrink-0 items-center justify-center rounded p-1 text-gray-400 opacity-0 cursor-pointer transition-colors duration-150 hover:bg-gray-200/80 hover:text-gray-900 focus:outline-none group-hover:opacity-100 data-[state=open]:bg-gray-200/80 data-[state=open]:text-gray-900 data-[state=open]:opacity-100">
              <MoreHorizontal size={14} strokeWidth={1.75} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={4}
            className="z-[120] min-w-[160px] rounded-lg border border-gray-200 bg-[#fbfaf9] p-1 shadow-lg">
            <DropdownMenuItem
              onSelect={() => setIsEditing(true)}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-gray-700 outline-none focus:bg-gray-100 focus:text-gray-900 data-[highlighted]:bg-gray-100 data-[highlighted]:text-gray-900">
              <Pencil size={13} strokeWidth={1.75} />
              {t('Agent.RenameAction')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                window.setTimeout(() => setIsConfirmingDelete(true), 0);
              }}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-red-600 outline-none focus:bg-red-50 data-[highlighted]:bg-red-50">
              <Trash2 size={13} strokeWidth={1.75} />
              {t('Agent.DeleteAction')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {createPortal(
        <AnimatePresence>
          {isConfirmingDelete && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: AGENT_DURATIONS.fast, ease: AGENT_EASING.outExpo }}
              style={{ background: 'rgba(0,0,0,0.5)' }}
              className="fixed inset-0 z-[130] flex items-center justify-center p-4"
              onClick={(e) => {
                if (e.target === e.currentTarget) setIsConfirmingDelete(false);
              }}>
              <motion.div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                initial={{ opacity: 0, scale: 0.96, y: 4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 4 }}
                transition={{ duration: AGENT_DURATIONS.fast, ease: AGENT_EASING.outExpo }}
                className="w-[min(420px,calc(100vw-2rem))] rounded-xl border border-gray-200 bg-[#fbfaf9] p-5 shadow-xl"
                style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
                <h2 id={titleId} className="text-base font-semibold text-gray-900">
                  {t('Agent.DeleteTitle')}
                </h2>
                <p className="mt-2 text-sm text-gray-600">{t('Agent.DeleteDescription', { title })}</p>
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setIsConfirmingDelete(false)}
                    className="cursor-pointer rounded-md border border-gray-200 bg-[#fbfaf9] px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-[#f4f1ed] focus:outline-none">
                    {t('Agent.Cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onDelete(conversation.conversation_id);
                      setIsConfirmingDelete(false);
                    }}
                    className="cursor-pointer rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 focus:outline-none">
                    {t('Agent.DeleteAction')}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
};

export default memo(NavSidebarItem);
